/**
 * Wie ein Zähler in Worten dasteht.
 *
 * **Rein und ohne React**, damit die Aussagen prüfbar sind: Welche Marke ein
 * Zähler bekommt, ist eine fachliche Entscheidung und keine Frage der
 * Darstellung.
 */
import { de } from "@hausmeter/i18n";
import type { ApiComponents } from "@hausmeter/sdk";

type Meter = ApiComponents["schemas"]["Meter"];
type MeterRegister = ApiComponents["schemas"]["MeterRegister"];

export type Tone = "success" | "warning" | "danger" | "neutral";

export interface MeterBadge {
  readonly label: string;
  readonly tone: Tone;
  /** Der ausführliche Satz — als `title` an der Marke und im Hinweis darüber. */
  readonly explanation: string;
}

/**
 * Der Zustand eines Zählers als Marke.
 *
 * **`draft` ist gelb und nicht rot.** Ein Zähler ohne Zählernummer ist der
 * Normalfall direkt nach dem ersten Wert — er sammelt bereits, ihm fehlt nur
 * die Angabe vom Typenschild. Rot wäre eine Störungsmeldung für einen Vorgang,
 * der planmäßig läuft; dieselbe Überlegung wie bei `never_seen` an einem frisch
 * gekoppelten Gerät (HM-042).
 *
 * **Und `replaced` ist grau, nicht grün.** Ein abgelöster Zähler ist weder in
 * Ordnung noch gestört — er hängt nicht mehr. Seine Historie zählt weiter, und
 * genau das sagt „Ersetzt".
 */
export function meterBadge(meter: Meter): MeterBadge {
  switch (meter.status) {
    case "active":
      return {
        label: de["meter.status.active"],
        tone: "success",
        explanation: "Vollständig eingerichtet: Zählernummer bekannt, Zählwerke angelegt.",
      };
    case "draft":
      return {
        label: de["meter.status.draft"],
        tone: "warning",
        explanation: de["meter.number.hint"],
      };
    case "replaced":
      return {
        label: de["meter.status.replaced"],
        tone: "neutral",
        explanation:
          "An dieser Messstelle hängt ein Nachfolger. Die Werte dieses Zählers bleiben " +
          "erhalten und gehen über die Messstelle in jede Auswertung ein.",
      };
    case "archived":
      return {
        label: de["meter.status.archived"],
        tone: "neutral",
        explanation:
          "Ausgebaut, ohne Nachfolger. Seine Zählerstände bleiben erhalten — gelöscht " +
          "wird an einem Zähler nichts.",
      };
  }
}

/** Die Bauart in Worten. Sie sagt, welche Zählwerke fachlich zu erwarten sind. */
export function meterKindLabel(kind: Meter["kind"], commodity: Meter["commodity"] = "electricity"): string {
  /*
   * Die Bauart beschreibt einen Stromzähler (Tarif, Richtung). Ein Wärmezähler
   * hat keine davon — „Eintarif, eine Richtung" an einem Wärmemengenzähler
   * wäre eine Auskunft über etwas, das er nicht hat (HM-192).
   */
  if (commodity === "heat") return "Wärmemengenzähler";
  switch (kind) {
    case "one_way":
      return "Eintarif, eine Richtung";
    case "two_way":
      return "Zweirichtungszähler";
    case "generation":
      return "Erzeugungszähler";
    case "sub_meter":
      return "Unterzähler";
  }
}

/** Die Rolle eines Zählwerks in Worten — der Text aus dem Fachkatalog. */
export function registerRoleLabel(role: MeterRegister["role"]): string {
  return de[`role.${role}`];
}

/**
 * Die Reihenfolge, in der Zählwerke stehen sollen.
 *
 * **Zählerstände zuerst, dann Momentanwerte** — dieselbe Ordnung wie bei den
 * Datenpunkten eines Geräts (HM-049). Wer einen Zähler ansieht, sucht zuerst
 * den Stand; Spannung und Strom sind Beiwerk. Innerhalb einer Gruppe entscheidet
 * die Rolle, damit L1 vor L2 steht und nicht die Kennzahl die Ordnung bestimmt.
 */
const UNIT_ORDER: Readonly<Record<string, number>> = { Wh: 0, W: 1, A: 2, V: 3 };

/**
 * Die Rollen in **fachlicher** Reihenfolge — dieselbe wie `REGISTER_ROLES` in
 * `@hausmeter/domain`.
 *
 * **Alphabetisch war falsch, und man sah es erst, als es einen Umschalter
 * gab** (HM-066): `energy_export_total` steht vor `energy_import_total`, und
 * damit stand an einem Zweirichtungszähler die **Einspeisung** vorn — der
 * Zähler öffnete auf dem Zählwerk, das die zweite Frage beantwortet. Gemeint
 * war die Ordnung, die die Domäne ohnehin führt: erst Bezug, dann seine Tarife,
 * dann Einspeisung, dann die Momentanwerte und deren Phasen.
 *
 * **Abgeschrieben und gegen die Quelle gehalten**, wie die Fristen der
 * Rechtstexte (HM-061): Diese App darf `@hausmeter/domain` nur für Typen
 * benutzen, und ein Fachpaket ins Bündel zu ziehen, damit eine Liste ihre
 * Reihenfolge kennt, lohnt nicht. `meter-labels.test.ts` hält beide zusammen —
 * wer in der Domäne eine Rolle ergänzt, bekommt einen roten Test statt einer
 * still ans Ende gerutschten Zeile.
 */
export const ROLE_ORDER = [
  "energy_import_total",
  "energy_import_t1",
  "energy_import_t2",
  "energy_export_total",
  "power_total",
  "power_l1",
  "power_l2",
  "power_l3",
  "voltage_l1",
  "voltage_l2",
  "voltage_l3",
  "current_l1",
  "current_l2",
  "current_l3",
  "heat_energy",
  "heat_power",
  "volume",
  "volume_flow",
  "temperature_flow",
  "temperature_return",
  "temperature_difference",
] as const;

/**
 * Die Kennzahl, wie sie neben einem Zählwerk steht — oder nur die Einheit.
 *
 * Bei Strom ist der Schlüssel eine OBIS-Kennzahl. Ein Wärmezählwerk hat keine
 * (HM-192): Sein Schlüssel ist die Rolle, und die steht ohnehin daneben. Eine
 * „Kennzahl" `heat_energy` hinter „Wärmemenge" wäre dasselbe Wort zweimal, und
 * dazu ein interner Name, den niemand lesen soll.
 */
export function registerKeyLabel(register: { readonly role: string; readonly obisCode: string; readonly unit: string }): string {
  return register.obisCode === register.role ? register.unit : `${register.obisCode} · ${register.unit}`;
}

export function sortRegisters(
  registers: readonly MeterRegister[],
): readonly MeterRegister[] {
  const rank = (role: string): number => {
    const index = ROLE_ORDER.indexOf(role as (typeof ROLE_ORDER)[number]);
    /* Eine Rolle, die es hier nicht gibt, wandert ans Ende und wird nicht
       geraten — sie steht dann hinter allem Bekannten statt irgendwo
       dazwischen (Regel 9). */
    return index === -1 ? ROLE_ORDER.length : index;
  };

  return [...registers].sort((a, b) => {
    const byUnit = (UNIT_ORDER[a.unit] ?? 9) - (UNIT_ORDER[b.unit] ?? 9);
    if (byUnit !== 0) return byUnit;
    const byRole = rank(a.role) - rank(b.role);
    if (byRole !== 0) return byRole;
    // Gleiche Rolle, verschiedene Kennzahl: irgendeine feste Ordnung ist besser
    // als eine, die sich bei jedem Laden ändern kann.
    return a.obisCode.localeCompare(b.obisCode);
  });
}

/**
 * Wie viele Zählwerke eines Zählers gerade eine Quelle haben.
 *
 * **Die Frage, die man an einem Zähler wirklich hat:** Kommt hier etwas an? Ein
 * Zählwerk ohne Quelle sammelt nichts — nach einem Gerätetausch ist das der
 * Normalfall, bis jemand neu bindet.
 */
export function boundRegisters(meter: Meter): number {
  return meter.registers.filter((register) => register.source !== null).length;
}

/**
 * Was in der **Kopfleiste** steht, wenn ein Zähler geöffnet ist (HM-072).
 *
 * **Der Entwurf legt Name und Fakten in die Kopfleiste des Rahmens**
 * (`ui_kits/dashboard/index.html`, `TopBar`) und lässt die Seite ohne eigene
 * Kopfzeile beginnen. HM-068 hatte das noch zurückgestellt — „das ist eine
 * Änderung am Rahmen und nicht an dieser Seite" —, und der Einwand stimmte
 * gegen den damaligen Umfang: Die Kopfzeile trug damals noch das Abzeichen und
 * „Gerät öffnen". Seit die ausgezogen sind (HM-071), stand dort ein Name über
 * einer Faktenzeile, und genau die Form hat die Kopfleiste ohnehin — zweimal
 * untereinander, einmal mit „Zähler" und einmal mit dem Namen des Zählers.
 *
 * **Die Formatierung bleibt bei den Zählern.** Der Rahmen ruft diese Funktion
 * und weiß nicht, wie eine Bauart heißt; sonst stünde die Zuordnung an einer
 * Stelle, die von Zählern nichts wissen soll.
 *
 * Ein **Einbauort**, wie ihn der Entwurf an dritter Stelle führt, existiert im
 * Datenmodell nicht (HM-068) — die Zahl der Zählwerke steht dafür, weil sie an
 * dieser Stelle die Frage beantwortet, die man sonst durch Scrollen klärt.
 */
/**
 * Die Zählernummer — oder, wo sie fehlt, warum (HM-112).
 *
 * **„ohne Zählernummer" und nicht „—".** Ein Strich sähe aus, als hätte niemand
 * nachgesehen; hier ist das Fehlen die Aussage. **Und „Messwerk im Gerät" statt
 * „ohne Zählernummer"**, wo es keine geben kann: Der Energiezähler einer
 * Wallbox hat kein Typenschild, und ein Satz, der eine fehlende Angabe
 * nahelegt, schickte jemanden auf die Suche nach etwas, das es nicht gibt.
 */
export function meterNumberText(meter: {
  meterNumber: string | null;
  builtIntoDevice: boolean;
  /** Ein virtueller Erzeugungszähler (HM-123) hat kein Typenschild — sein Stand ist die Summe seiner Quellen. */
  virtual?: boolean;
}): string {
  if (meter.virtual === true) return "virtuell";
  return meter.meterNumber ?? (meter.builtIntoDevice ? "Messwerk im Gerät" : "ohne Zählernummer");
}

export function meterHeadline(meter: Meter): { title: string; subtitle: string } {
  const parts = [
    meterKindLabel(meter.kind, meter.commodity),
    meterNumberText(meter),
    meter.registers.length === 1 ? "1 Zählwerk" : `${meter.registers.length} Zählwerke`,
  ];

  return { title: meter.label, subtitle: parts.join(" · ") };
}

/**
 * Angaben einer Zeile mit „ · " verbunden, **jede nur einmal** (UI-Durchsicht U8).
 * Bauart und Rolle im Messkonzept sind oft dasselbe Wort, und der Name eines
 * Zählers heißt nicht selten wie sein Steckplatz: „Erzeugungszähler · Erzeugungszähler"
 * las sich wie ein Fehler. Was schon im Namen steht (`besides`), entfällt ebenfalls.
 */
export function factLine(parts: readonly (string | null | undefined)[], besides: readonly string[] = []): string {
  const seen = new Set(besides.map((entry) => entry.trim().toLowerCase()));
  const kept: string[] = [];
  for (const part of parts) {
    const text = part?.trim();
    if (!text || seen.has(text.toLowerCase())) continue;
    seen.add(text.toLowerCase());
    kept.push(text);
  }
  return kept.join(" · ");
}

/**
 * **Leistung eines Zählers ohne Vorzeichen** (UI-Durchsicht U3). Eine Leistung
 * ist positiv in Bezugsrichtung ihres Zählers; „−2,01 kW“ las niemand als
 * Einspeisung. Am Netzanschluss steht deshalb das Wort, an jedem anderen Zähler
 * der Betrag — welche Richtung dort Erzeugung heißt, hängt an der Bauart des
 * Zählers und wird nicht geraten (Regel 9).
 */
export function directedPower(watts: number, atConnection: boolean): { watts: number; word: string | null } {
  if (!atConnection) return { watts: Math.abs(watts), word: null };
  return watts < 0 ? { watts: -watts, word: "Einspeisung" } : { watts, word: "Bezug" };
}
